<template>
  <div :class="['bubble', bubbleClass]">
    <template v-for="(part, i) in message.parts">
      <template v-if="part.type === 'text' && part.text">
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div
          v-if="message.role === 'assistant'"
          :key="i"
          class="text markdown"
          dir="auto"
          v-html="renderMarkdown(part.text)"
        ></div>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <p
          v-else
          :key="i"
          class="text"
          dir="auto"
          v-html="renderText(part.text)"
        ></p>
      </template>
      <ActionToolApproval
        v-else-if="part.type === 'tool-invocation'"
        :key="i"
        :tool-name="part.toolInvocation.toolName"
        :args="part.toolInvocation.args"
        :state="part.toolInvocation.state"
        :result="part.toolInvocation.result"
        :on-approve="onApprove"
        :on-approve-and-trust="onApproveAndTrust"
        :on-reject="onReject"
        :is-already-trusted="isAlreadyTrusted"
      />
    </template>
  </div>
</template>

<script>
import MarkdownIt from "markdown-it";
import ActionToolApproval from "./ActionToolApproval.vue";

const md = new MarkdownIt({ html: false, linkify: true, breaks: true });

/**
 * The LLM sometimes emits "typographic" hyphens inside URLs — U+2010 HYPHEN,
 * U+2011 NON-BREAKING HYPHEN, U+00AD SOFT HYPHEN — instead of the ASCII '-'.
 * markdown-it's linkifier only recognises ASCII '-', so a URL like
 * https://my-store-2.zidtest.com/ gets linked only up to the first fancy
 * hyphen ("https://my") and the rest renders as plain text. Normalise those
 * three hyphen-variants to ASCII '-' before rendering. En dash (U+2013) and
 * em dash (U+2014) are deliberately left alone — they are legitimate prose
 * punctuation, not hyphens.
 */
function normalizeHyphens(text) {
  return String(text).replace(/[‐‑­]/g, "-");
}

const defaultLinkOpen =
  md.renderer.rules.link_open ||
  function (tokens, idx, options, env, self) {
    return self.renderToken(tokens, idx, options);
  };
md.renderer.rules.link_open = function (tokens, idx, options, env, self) {
  const token = tokens[idx];
  const targetIdx = token.attrIndex("target");
  if (targetIdx < 0) token.attrPush(["target", "_blank"]);
  else token.attrs[targetIdx][1] = "_blank";
  const relIdx = token.attrIndex("rel");
  if (relIdx < 0) token.attrPush(["rel", "noopener noreferrer"]);
  else token.attrs[relIdx][1] = "noopener noreferrer";
  return defaultLinkOpen(tokens, idx, options, env, self);
};

export default {
  name: "MessageBubble",
  components: { ActionToolApproval },
  props: {
    message: { type: Object, required: true },
    onApprove: { type: Function, default: null },
    onApproveAndTrust: { type: Function, default: null },
    onReject: { type: Function, default: null },
    isAlreadyTrusted: { type: Boolean, default: false },
  },
  computed: {
    bubbleClass() {
      if (this.message.role === "user") return "user";
      return "assistant";
    },
  },
  methods: {
    /**
     * Escape any HTML the model could emit, then turn http(s) URLs into
     * <a target="_blank" rel="noopener noreferrer"> tags. Newlines are
     * preserved by white-space: pre-wrap on the .text class.
     *
     * Why escape first: the LLM output is officer-only, but officer-typed
     * input is also rendered through this path, and we never want bare
     * markup to render as HTML. The URL regex then runs against the safe
     * escaped string.
     */
    renderText(raw) {
      const escaped = normalizeHyphens(raw)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
      // URL regex: http/https, no whitespace, stop at common trailing punctuation.
      // Capture the URL, then strip a trailing punctuation char if it looks like
      // sentence terminator rather than part of the URL.
      return escaped.replace(/(https?:\/\/[^\s<>"'`]+)/g, (match) => {
        let url = match;
        let trailing = "";
        // Pull off a trailing . , ; : ! ? ) ] } that's almost certainly punctuation.
        const trailingMatch = url.match(/([.,;:!?)}\]]+)$/);
        if (trailingMatch) {
          trailing = trailingMatch[1];
          url = url.slice(0, -trailing.length);
        }
        return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>${trailing}`;
      });
    },
    // Render assistant LLM output as markdown (html disabled, links open in new tab).
    renderMarkdown(raw) {
      return md.render(normalizeHyphens(raw));
    },
  },
};
</script>

<style scoped>
.bubble {
  margin-bottom: 12px;
  max-width: 90%;
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.5;
}
.bubble.user {
  background: #eef2ff;
  color: #1e293b;
  margin-inline-start: auto;
  border-bottom-right-radius: 4px;
}
[dir="rtl"] .bubble.user {
  border-bottom-right-radius: 12px;
  border-bottom-left-radius: 4px;
}
.bubble.assistant {
  background: #f3f4f6;
  color: #111827;
  border-bottom-left-radius: 4px;
}
[dir="rtl"] .bubble.assistant {
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 4px;
}
.text {
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
}
.text.markdown {
  white-space: normal;
}
.text >>> a {
  color: #4f46e5;
  text-decoration: underline;
  word-break: break-all;
}
.text >>> a:hover {
  color: #4338ca;
}
.text.markdown >>> ul,
.text.markdown >>> ol {
  padding-inline-start: 20px;
  margin: 4px 0;
}
.text.markdown >>> li {
  margin: 2px 0;
}
.text.markdown >>> p {
  margin: 4px 0;
}
.text.markdown >>> code {
  background: rgba(0, 0, 0, 0.06);
  padding: 1px 5px;
  border-radius: 4px;
  font-size: 0.9em;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
.text.markdown >>> pre {
  background: rgba(0, 0, 0, 0.06);
  padding: 8px 10px;
  border-radius: 6px;
  overflow-x: auto;
  margin: 6px 0;
  font-size: 0.9em;
}
.text.markdown >>> pre code {
  background: inherit;
  padding: 0;
}
/* Safety net: the model is told to prefer card-style lists over tables in
   this narrow panel, but if a markdown table still slips through it must
   stay inside the bubble and scroll instead of bleeding past the panel. */
.text.markdown >>> table {
  display: block;
  max-width: 100%;
  overflow-x: auto;
  border-collapse: collapse;
  margin: 6px 0;
  font-size: 0.92em;
}
.text.markdown >>> th,
.text.markdown >>> td {
  border: 1px solid #d1d5db;
  padding: 4px 8px;
  text-align: start;
  white-space: nowrap;
}
.text.markdown >>> thead th {
  background: rgba(0, 0, 0, 0.04);
  font-weight: 600;
}
.text.markdown >>> h1 {
  margin: 8px 0 4px;
  font-weight: 600;
  font-size: 1.15em;
}
.text.markdown >>> h2 {
  margin: 8px 0 4px;
  font-weight: 600;
  font-size: 1.1em;
}
.text.markdown >>> h3 {
  margin: 8px 0 4px;
  font-weight: 600;
  font-size: 1.05em;
}
.text.markdown >>> h4 {
  margin: 8px 0 4px;
  font-weight: 600;
  font-size: 1em;
}
.text.markdown >>> blockquote {
  border-inline-start: 3px solid #d1d5db;
  padding-inline-start: 8px;
  margin: 4px 0;
  color: #4b5563;
}
.text.markdown >>> :first-child {
  margin-top: 0;
}
.text.markdown >>> :last-child {
  margin-bottom: 0;
}
</style>
